import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { groupService } from '../../services/groupService';
import { toast } from '../ui/Toaster';
import { FeaturedImageUpload } from '../ui';
import type { Group } from '../../types';

interface EditGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: Group | null;
  onGroupUpdated: (updatedGroup: Group) => void;
}

const EditGroupModal: React.FC<EditGroupModalProps> = ({
  isOpen,
  onClose,
  group,
  onGroupUpdated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens with new group
  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || '');
      setFeaturedImage(null);
      setErrors({});
    }
  }, [group]);

  if (!isOpen || !group) {
    return null;
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Groepsnaam is verplicht';
    } else if (name.length < 3) {
      newErrors.name = 'Groepsnaam moet minstens 3 tekens bevatten';
    } else if (name.length > 50) {
      newErrors.name = 'Groepsnaam mag maximaal 50 tekens bevatten';
    }

    if (description && description.length > 500) {
      newErrors.description = 'Beschrijving mag maximaal 500 tekens bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      let updateData: { name: string; description?: string } | FormData;

      // Use FormData if image is being uploaded
      if (featuredImage) {
        const formData = new FormData();
        formData.append('name', name.trim());
        if (description.trim()) {
          formData.append('description', description.trim());
        }
        formData.append('featuredImage', featuredImage);
        updateData = formData;
      } else {
        // Use regular object for text-only updates
        updateData = {
          name: name.trim(),
        };

        if (description.trim()) {
          updateData.description = description.trim();
        }
      }

      const updatedGroup = await groupService.updateGroup(group.id, updateData);

      toast.success('Succes', 'Groep succesvol bijgewerkt');
      onGroupUpdated(updatedGroup);
      onClose();
    } catch (error: any) {
      console.error('Failed to update group:', error);
      toast.error('Fout', error.message || 'Kon groep niet bijwerken');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-canvas/70 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-surface border border-white/[.08] rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-panel transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-fg-bright">
              Groep bewerken
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 min-h-[44px] min-w-[44px] rounded-xl text-fg-muted hover:text-fg-bright disabled:opacity-50 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Group Name */}
            <div>
              <label htmlFor="group-name" className="block text-sm font-semibold text-fg-soft mb-2">
                Groepsnaam *
              </label>
              <input
                id="group-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    setErrors(prev => ({ ...prev, name: '' }));
                  }
                }}
                disabled={isLoading}
                className={`sf-input min-h-[44px] disabled:opacity-50 ${
                  errors.name ? 'border-neon-pink/60' : ''
                }`}
                placeholder="Voer groepsnaam in"
                maxLength={100}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-neon-pink-soft">{errors.name}</p>
              )}
            </div>

            {/* Group Description */}
            <div>
              <label htmlFor="group-description" className="block text-sm font-semibold text-fg-soft mb-2">
                Beschrijving
              </label>
              <textarea
                id="group-description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors(prev => ({ ...prev, description: '' }));
                  }
                }}
                disabled={isLoading}
                rows={3}
                className={`sf-input disabled:opacity-50 ${
                  errors.description ? 'border-neon-pink/60' : ''
                }`}
                placeholder="Beschrijf je groep (optioneel)"
                maxLength={500}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-neon-pink-soft">{errors.description}</p>
              )}
              <p className="mt-1 text-right font-mono text-xs text-fg-meta">
                {description.length}/500 karakters
              </p>
            </div>

            {/* Group Image */}
            <div>
              <label className="block text-sm font-semibold text-fg-soft mb-2">
                Groepsafbeelding
              </label>
              <FeaturedImageUpload
                onFileSelect={setFeaturedImage}
                currentImageUrl={group.featuredImage ?? undefined}
                {...(errors.featuredImage && { error: errors.featuredImage })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="sf-btn-secondary w-full sm:w-auto px-4 py-3 min-h-[44px] text-sm"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !name.trim()}
              className="sf-btn-primary w-full sm:w-auto px-4 py-3 min-h-[44px] text-sm"
            >
              <Save className="h-4 w-4" />
              <span>{isLoading ? 'Opslaan...' : 'Wijzigingen opslaan'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditGroupModal;